import { z } from 'zod';

export const blockIds = [
  'overview',
  'funnel',
  'agreements',
  'objections',
  'handoffs',
  'timing',
  'actions',
] as const;
export const blockSchema = z.enum(blockIds);
export type BlockId = z.infer<typeof blockSchema>;
export const briefSchema = z
  .object({
    title: z.string().min(1).max(100),
    start: z.iso.date(),
    end: z.iso.date(),
    focus: z.enum(['closing', 'engagement', 'agreements']),
    blocks: z.array(blockSchema).min(1).max(7),
    request: z.string().max(2000).optional(),
  })
  .refine((value) => value.start <= value.end, 'O início deve ser anterior ao fim.');
export type ReportBrief = z.infer<typeof briefSchema>;
export const planSchema = z.object({
  supported: z.boolean(),
  summary: z.string().max(1000),
  title: z.string().min(1).max(100),
  focus: z.enum(['closing', 'engagement', 'agreements']),
  blocks: z.array(blockSchema).min(1).max(7),
});
export type ReportPlan = z.infer<typeof planSchema>;
export const preferencesSchema = z.object({
  remember: z.boolean(),
  focus: z.enum(['closing', 'engagement', 'agreements']),
  blocks: z.array(blockSchema).min(1).max(7),
});
export type ReportPreferences = z.infer<typeof preferencesSchema>;
const metricSchema = z.object({
  label: z.string().max(80),
  value: z.string().max(40),
  detail: z.string().max(160),
});
export const reportBlockSchema = z.object({
  id: blockSchema,
  title: z.string().max(100),
  summary: z.string().max(600),
  metrics: z.array(metricSchema).max(4).optional(),
  bars: z
    .array(
      z.object({
        label: z.string().max(100),
        value: z.number().nonnegative(),
        total: z.number().positive(),
      }),
    )
    .max(6)
    .optional(),
  rows: z
    .array(z.array(z.string().max(160)).max(5))
    .max(6)
    .optional(),
  columns: z.array(z.string().max(60)).max(5).optional(),
  notes: z.array(z.string().max(300)).max(5).optional(),
});
export type ReportBlock = z.infer<typeof reportBlockSchema>;
export const jobSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  version: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  status: z.enum(['queued', 'running', 'completed', 'empty', 'failed', 'cancelled']),
  stage: z.number().int().min(0).max(4),
  brief: briefSchema,
  blocks: z.array(reportBlockSchema).max(7),
  demo: z.boolean(),
  warning: z.string().max(500).optional(),
  notificationEnabled: z.boolean(),
});
export type ReportJob = z.infer<typeof jobSchema>;
export const snapshotSchema = z.object({
  jobs: z.array(jobSchema).max(50),
  preferences: preferencesSchema,
});
export type ReportSnapshot = z.infer<typeof snapshotSchema>;
export interface ReportsGateway {
  interpret(workspaceId: string, text: string, currentBrief?: ReportBrief): Promise<ReportPlan>;
  load(workspaceId: string): Promise<ReportSnapshot>;
  create(workspaceId: string, brief: ReportBrief, idempotencyKey: string): Promise<ReportJob>;
  get(workspaceId: string, id: string): Promise<ReportJob>;
  cancel(workspaceId: string, id: string): Promise<ReportJob>;
  preferences(workspaceId: string, preferences: ReportPreferences): Promise<ReportPreferences>;
}
